import { Panel, RoutedPanelProps } from '@/components/Layout/Panel/Panel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  useCreateLaborMutation,
  useLaborEntry,
  useUpdateLaborMutation,
  useWorkorder,
  useWorkorderLock,
} from '@/features/workorders/workorders.hooks';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

export const LaborDetailPanel = ({
  params,
  ...panelProps
}: RoutedPanelProps) => {
  const { workorderId, laborId } = params;
  const workorderRecnum = parseInt(workorderId);
  const isNew = laborId === 'new';

  const laborQuery = useLaborEntry(isNew ? 0 : parseInt(laborId));
  const workorderQuery = useWorkorder(workorderRecnum);
  const { locked: workorderLocked, reason: lockReason } = useWorkorderLock(
    workorderQuery.data,
  );
  const createMutation = useCreateLaborMutation(workorderRecnum);
  const updateMutation = useUpdateLaborMutation(workorderRecnum);

  // Form state
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [startTime, setStartTime] = useState('07:00');
  const [endTime, setEndTime] = useState('16:00');
  const [breakMinutes, setBreakMinutes] = useState(0);
  const [dayfee, setDayfee] = useState(false);

  // Load existing data
  useEffect(() => {
    if (!isNew && laborQuery.data) {
      const entry = laborQuery.data;
      setDate(format(entry.date_start, 'yyyy-MM-dd'));
      setStartTime(format(entry.date_start, 'HH:mm'));
      setEndTime(format(entry.date_stop, 'HH:mm'));
      setBreakMinutes((entry.break ?? 0) * 60);
      setDayfee(entry.dayfee);
    }
  }, [isNew, laborQuery.data]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Format as local ISO strings (no timezone) for server compatibility
    const date_start = `${date}T${startTime}:00`;
    const date_stop = `${date}T${endTime}:00`;
    const breakHours = breakMinutes / 60;

    if (isNew) {
      await createMutation.mutateAsync({
        workorder_id: workorderRecnum,
        date_start,
        date_stop,
        break: breakHours,
        dayfee,
      });
    } else {
      await updateMutation.mutateAsync({
        recnum: parseInt(laborId),
        data: {
          date_start,
          date_stop,
          break: breakHours,
          dayfee,
        },
      });
    }

    window.history.back();
  };

  const isLoading =
    (!isNew && laborQuery.isLoading) || workorderQuery.isLoading;
  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isLocked = workorderLocked || (!isNew && laborQuery.data?.locked);

  const title = isNew ? 'Add Labor Entry' : 'Edit Labor Entry';

  return (
    <Panel {...panelProps} title={title}>
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : isLocked ? (
        <div className="text-muted-foreground py-8 text-center">
          {lockReason ?? 'This labor entry is locked and cannot be edited.'}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="date">Date</Label>
            <Input
              id="date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startTime">Start Time</Label>
              <Input
                id="startTime"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endTime">End Time</Label>
              <Input
                id="endTime"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="break">Break (minutes)</Label>
            <Input
              id="break"
              type="number"
              min="0"
              max="480"
              step="15"
              value={breakMinutes}
              onChange={(e) => setBreakMinutes(parseInt(e.target.value) || 0)}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch id="dayfee" checked={dayfee} onCheckedChange={setDayfee} />
            <Label htmlFor="dayfee">Day Fee Applies</Label>
          </div>

          <div className="flex gap-2">
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => window.history.back()}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Panel>
  );
};
